import React, { useState } from 'react';
import { createPaymentMethod, useForm } from './provider';
import { CardElement } from '@stripe/react-stripe-js';
import axios from "../../utils/requests";
import { ArrowLeftIcon, CreditCardIcon, LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const CARD_OPTIONS = {
  hidePostalCode: false,
  iconStyle: 'solid',
  style: {
    base: {
      color: '#1f2937',
      fontSize: '16px',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      ':focus': {
        color: '#1f2937',
      },
    },
  },
};

export const CreatePaymentMethodPage = (props) => {
  const { state, dispatch, stripe, elements } = useForm();
  const { errors, loading } = state;
  const [errorClass, setErrorClass] = useState('');

  const handleChange = (event) => {
    const { error } = event;
    if (error) {
      setErrorClass('border-red-300 ring-2 ring-red-100');
    } else {
      setErrorClass('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch({ type: "TOGGLE_LOADING" });
    const resp = await createPaymentMethod({ state, dispatch, stripe, elements });

    if (resp.error) {
      dispatch({ type: "TOGGLE_LOADING" });
      dispatch({
        type: "SET_ERRORS",
        errors: { base: [resp.error.message] },
      });
      return;
    }

    try {
      await axios.post(state.create_card_path, {
        payment_method: resp.paymentMethod
      });
    } catch (error) {
      dispatch({ type: "TOGGLE_LOADING" });
      dispatch({ type: "SET_ERRORS", errors: { base: error.response.data.error } });
      return;
    }

    dispatch({ type: "TOGGLE_LOADING" });
    dispatch({
      type: "SET_PAYMENT_METHOD",
      paymentMethod: resp.paymentMethod,
    });

    props.goToNamedStep('card_payment_page');
  };

  const btnLoading = !stripe || loading;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => props.goToNamedStep("pick_payment_method")}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Add Payment Card</h2>
          <p className="text-sm text-gray-500">Enter your card details below</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Card Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Information
          </label>
          <div className={`p-4 rounded-xl border-2 transition-colors ${errorClass || 'border-gray-200 focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100'}`}>
            <CardElement options={CARD_OPTIONS} onChange={handleChange} />
          </div>
        </div>

        {/* Error Message */}
        {errors && errors.base && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700">{errors.base}</p>
          </div>
        )}

        {/* Security Notice */}
        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
              <ShieldCheckIcon className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Secure Payment</p>
              <p className="text-xs text-gray-500 mt-1">
                Your payment information is encrypted and secure. We never store your full card number.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={btnLoading}
          className={`w-full py-4 px-6 rounded-xl text-white font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 ${
            btnLoading
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 shadow-lg shadow-indigo-500/25'
          }`}
        >
          {btnLoading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Saving Card...</span>
            </>
          ) : (
            <>
              <LockClosedIcon className="w-5 h-5" />
              <span>Save & Continue</span>
            </>
          )}
        </button>

        {/* Accepted Cards */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <span className="text-xs text-gray-400">Accepted cards:</span>
          <div className="flex items-center gap-2">
            <div className="w-10 h-6 rounded bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">VISA</span>
            </div>
            <div className="w-10 h-6 rounded bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">MC</span>
            </div>
            <div className="w-10 h-6 rounded bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">AMEX</span>
            </div>
            <div className="w-10 h-6 rounded bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">DISC</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// Keep this export for backwards compatibility if needed elsewhere
export const NewPaymentMethodSection = CreatePaymentMethodPage;
